import { Card, CardContent, Typography, Box, IconButton, Tooltip } from "@mui/material";
import { BugReport } from "@mui/icons-material";
import Chart from "react-apexcharts";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";
import { DashboardWidget, AggregateRequest } from "../../types/dashboard/DashboardConfig";
import { useDashboardAggregate } from "../../hooks/useDashboardAggregate";
import LoadingIndicator from "../../components/common/LoadingIndicator/LoadingIndicator";
import { numberWithCommas } from "../../helpers/numberWithCommas";
import { buildChartConfig } from "./buildChartConfig";
import { useDashboardDrill } from "../../hooks/useDashboardDrill";
import {
  resolveDrillDescriptor,
  canDrillCustomWidget,
} from "../../helpers/dashboardDrill";

interface CustomAggregateWidgetProps {
  widget: DashboardWidget;
}

const DebugButton = ({ onClick }: Readonly<{ onClick: () => void }>) => (
  <Tooltip title="Copy API request to clipboard">
    <IconButton size="small" onClick={onClick} sx={{ position: "absolute", top: 4, right: 4, zIndex: 5, opacity: 0.4, "&:hover": { opacity: 1 } }}>
      <BugReport fontSize="small" />
    </IconButton>
  </Tooltip>
);

const ChartCard = ({ title, onDebug, children }: Readonly<{ title: string; onDebug: () => void; children: React.ReactNode }>) => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
    <DebugButton onClick={onDebug} />
    <CardContent
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        "&:last-child": { pb: 1 },
      }}
    >
      <Typography variant="h6" color="primary" sx={{ flexShrink: 0 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </CardContent>
  </Card>
);

function CustomAggregateWidget({ widget }: Readonly<CustomAggregateWidgetProps>) {
  const { customerId } = useParams();
  const theme = useTheme();
  const config = widget.config;

  const aggregateRequest: AggregateRequest | null = useMemo(() => {
    if (!config) return null;
    return {
      aggregationType: config.aggregationType ?? "terms",
      field: config.aggregationType === "count" ? undefined : config.field,
      interval: config.interval,
      size: config.size,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      from: config.filters?.from,
      to: config.filters?.to,
      agentName: config.filters?.agentName,
      direction: config.filters?.direction,
      interactionType: config.filters?.interactionType,
      skill: config.filters?.skill,
      group: config.filters?.group,
      tags: config.filters?.tags,
    };
  }, [config]);

  const { data, isLoading } = useDashboardAggregate(
    customerId,
    aggregateRequest,
    widget.id
  );

  const handleCopyDebug = () => {
    if (!aggregateRequest) return;
    const payload = {
      url: `/v1/customers/${customerId}/dashboard/aggregate`,
      body: aggregateRequest,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  };

  const chartType = config?.chartType ?? "bar";
  const primaryColor = theme.palette.customColors?.wDarkBlue;
  const customColors = useMemo(
    () => (theme.palette.customColors ? Object.values(theme.palette.customColors) : undefined),
    [theme.palette.customColors],
  );

  const { drill, canDrill } = useDashboardDrill("v1", false);
  const widgetFilters = config?.filters ?? {};
  const isDrillable =
    canDrill &&
    !isLoading &&
    !!data &&
    data.labels.length >= 0 &&
    canDrillCustomWidget(widget);

  const handleChartClick = (_event: any, _ctx: any, configClick: any) => {
    if (!isDrillable || !data) return;
    const idx = configClick?.dataPointIndex;
    // Bar/pie click events fire only on data points (idx >= 0). Line chart's
    // chart.events.click fires anywhere on the chart area; idx is the closest
    // tracked point or -1 if no point is being tracked.
    if (idx === undefined || idx === -1) return;
    const desc = resolveDrillDescriptor(widget, idx, data.labels);
    if (!desc) return;
    drill(desc, widgetFilters);
  };

  const handleLegendClick = (_ctx: any, seriesIndex: number) => {
    if (!isDrillable || !data) return;
    if (seriesIndex === undefined || seriesIndex < 0) return;
    const desc = resolveDrillDescriptor(widget, seriesIndex, data.labels);
    if (!desc) return;
    drill(desc, widgetFilters);
  };

  const handleKpiTileClick = (event: React.MouseEvent) => {
    if (!isDrillable) return;
    drill({ kind: "none" }, widgetFilters, {
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
      button: event.button,
    });
  };

  // Memoize chart config so ApexCharts doesn't redraw on parent re-renders
  const chartConfig = useMemo(() => {
    if (!data) return null;
    const baseConfig = buildChartConfig(data, chartType, widget.title, primaryColor, customColors);
    if (isDrillable && baseConfig) {
      // Use `click` for line charts (fires anywhere in the chart area, with
      // dataPointIndex set to the active tracked point) and `dataPointSelection`
      // for bar/pie (only fires on actual data elements). Pick one per chart
      // type to avoid double-fires.
      const events =
        chartType === "line"
          ? { click: handleChartClick }
          : { dataPointSelection: handleChartClick };
      // Pie legend items: drill instead of toggling slice visibility.
      if (chartType === "pie") {
        (events as any).legendClick = handleLegendClick;
        baseConfig.options.legend = {
          ...(baseConfig.options.legend ?? {}),
          onItemClick: { toggleDataSeries: false },
        };
      }
      baseConfig.options.chart = {
        ...(baseConfig.options.chart ?? {}),
        events: {
          ...(baseConfig.options.chart?.events ?? {}),
          ...events,
        },
      };
    }
    return baseConfig;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, chartType, widget.title, primaryColor, customColors, isDrillable]);

  if (isLoading) {
    return (
      <Card sx={{ height: "100%", position: "relative" }}>
        <DebugButton onClick={handleCopyDebug} />
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <LoadingIndicator />
        </CardContent>
      </Card>
    );
  }

  // KPI: show a single large number (must be checked before !chartConfig since KPI has no chart)
  if (chartType === "kpi") {
    return (
      <Card
        sx={{
          height: "100%",
          position: "relative",
          cursor: isDrillable ? "pointer" : "default",
          ...(isDrillable && {
            "&:hover": { boxShadow: 3 },
          }),
        }}
        onClick={handleKpiTileClick}
      >
        <DebugButton onClick={handleCopyDebug} />
        <CardContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
          <Typography variant="h6" color="primary">
            {widget.title}
          </Typography>
          <Typography variant="h3" color="secondary" sx={{ mt: 1 }}>
            {numberWithCommas(data?.total)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!chartConfig) {
    return (
      <Card sx={{ height: "100%", position: "relative" }}>
        <DebugButton onClick={handleCopyDebug} />
        <CardContent>
          <Typography variant="h6" color="primary">
            {widget.title}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <ChartCard title={widget.title} onDebug={handleCopyDebug}>
      <Box
        sx={{
          height: "100%",
          ...(isDrillable && {
            // ApexCharts inner elements set their own cursor; force pointer
            // on the wrapper and every descendant so bar areas, pie slices,
            // line markers, and the line chart's svg surface all show it.
            cursor: "pointer",
            "& .apexcharts-canvas, & .apexcharts-canvas *": {
              cursor: "pointer",
            },
          }),
        }}
      >
        <Chart
          options={chartConfig.options}
          series={chartConfig.series}
          type={chartConfig.type}
          height="100%"
        />
      </Box>
    </ChartCard>
  );
}

export default React.memo(CustomAggregateWidget);
